import React, { useEffect, useState } from "react";
import "./LeadDetails.css";
import { useNavigate, useParams ,useLocation} from "react-router-dom";
import axios from "axios";
interface TimelineItem {
  id: string;
  type: string;
  agent_name: string | null;
  role_id?: number;
  timeline_time: string;
  otherDetails: {
    source_of_lead?: string;
    client_name: string;
    phone?: string;
    generated_for_project?: string;
    activity?: string;
    remark?: string;
    despositions_name?: string;
    recording_link?: string;
    call_duration?: string;
    meeting_date?: string;
    meeting_time?: string;
    address?: string;
    [key: string]: any;
  };
  profile_image: string | null;
  time_diff: string;
}

interface LeadData {
  hasPermission: number;
  timeline: TimelineItem[];
  user: {
    name: string;
    role: {
      display_name: string;
    };
  };
  closedLeadDetails: any;
  getLeadRatingData: any;
}

const LeadDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [leadData, setLeadData] = useState<LeadData | null>(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const { raw_lead_id, raw_leads_remarks,customer_address,raw_leads_buying_location,raw_leads_property_type,raw_leads_configuration,raw_leads_budget_max,raw_leads_budget_min, bhk, city,voice_note } = location.state || {};
  useEffect(() => {
    const fetchLeadDetails = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          alert("Session expired, please login again");
          navigate("/");
          return;
        }
  
        const res = await axios.get(
          `https://www.crm.brickfolio.in/api/leads/timeline/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          }
        );
        setLeadData(res.data);
      } catch (error: any) {
        console.error("Error fetching lead details:", error);
  
        if (error?.response?.status === 401) {
          localStorage.removeItem("authToken");
          alert("Session expired, please login again");
          navigate("/");
        } else {
          alert("Cannot fetch lead details");
        }
      } finally {
        setLoading(false);
      }
    };
  
    fetchLeadDetails();
  }, [id, navigate]);

  const getRoleDisplayName = (roleId?: number): string => {
    switch (roleId) {
      case 4:
        return "Sales Agent";
      case 5:
        return "Presales Agent";
      default:
        return "Backend User";
    }
  };

  if (loading) return <p className="loadingText">Loading lead details...</p>;
  if (!leadData) return <p className="loadingText">No lead data found</p>;

  // Get lead information from the first timeline item
  const firstTimelineItem = leadData.timeline[0];
  const clientName = firstTimelineItem?.otherDetails.client_name || "Unknown Client";

  const formatTimelineItem = (item: TimelineItem) => {
    const { type, timeline_time, otherDetails, time_diff, role_id } = item;

    const roleDisplayName = getRoleDisplayName(role_id);

    switch (type) {
      case "leadgen":
        return {
          icon: "📥",
          title: "Lead Generated",
          description: `Source: ${otherDetails.source_of_lead || "Unknown"}`,
          time: timeline_time,
          role: roleDisplayName,
          timeDiff: time_diff,
        };

      case "system_call":
        return {
          icon: "📞",
          title: "Call Attempted",
          description: "System call was made to client",
          time: timeline_time,
          role: roleDisplayName,
          timeDiff: time_diff,
        };

      case "sales_recording_call":
        return {
          icon: "🎙️",
          title: "Sales Recording",
          description: "Sales call recording available",
          time: timeline_time,
          role: roleDisplayName,
          timeDiff: time_diff,
        };

      case "lead":
        return {
          icon: "📋",
          title: "Lead Details Updated",
          description: otherDetails.requirement || "Lead requirements updated",
          time: timeline_time,
          role: roleDisplayName,
          timeDiff: time_diff,
        };

      case "meeting":
        return {
          icon: "📅",
          title: "Meeting Scheduled",
          description: `Meeting at ${otherDetails.address || "Unknown location"} on ${otherDetails.meeting_date} ${otherDetails.meeting_time}`,
          time: timeline_time,
          role: roleDisplayName,
          timeDiff: time_diff,
        };

      case "recording":
        return {
          icon: "🎵",
          title: "Recording Added",
          description: `Call duration: ${otherDetails.call_duration || "N/A"} seconds`,
          time: timeline_time,
          role: roleDisplayName,
          timeDiff: time_diff,
        };

      case "activity":
        return {
          icon: "⚡",
          title: "Activity",
          description: otherDetails.activity || "System activity",
          time: timeline_time,
          role: roleDisplayName,
          timeDiff: time_diff,
        };

      case "disposition":
        return {
          icon: "🏷️",
          title: otherDetails.despositions_name || "Disposition Updated",
          description: otherDetails.remark || "Status updated",
          time: timeline_time,
          role: roleDisplayName,
          timeDiff: time_diff,
        };

      default:
        return {
          icon: "🔹",
          title: type,
          description: "Activity performed",
          time: timeline_time,
          role: roleDisplayName,
          timeDiff: time_diff,
        };
    }
  };

  const latestDisposition = leadData.timeline
    .filter(item => item.type === "disposition")
    .pop();

  const currentStatus = latestDisposition?.otherDetails.despositions_name || "New Lead";

  return (
    <div className="leadPage">
      <div className="leadDetailsContainer">
        <div className="leadHeader">
          <div className="headerTop" onClick={() => navigate("/dashboard")}>
            ← Back to Leads
          </div>

          <div className="leadProfile">
            <div className="avatar">👤</div>
            <div className="profileInfo">
              <h2>{clientName}</h2>
              <button className={`followBtn ${currentStatus.toLowerCase().replace(/\s+/g, '-')}`}>
                {currentStatus}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="contentContainer">
      <div className="cardBox">
          <h3>Contact Information</h3>
          <div className="cardBox">
          <div className="grid3">
            <div className="infoItem">
              <label>Lead ID</label>
              <p>{raw_lead_id || "N/A"}</p>
            </div>
            <div className="infoItem">
              <label>BHK</label>
              <p>{bhk || "N/A"}</p>
            </div>
            <div className="infoItem">
              <label>City</label>
              <p>{city || "N/A"}</p>
            </div>
            <div className="infoItem">
              <label>Property Type</label>
              <p>{raw_leads_property_type || "N/A"}</p>
            </div>
            <div className="infoItem">
              <label>Configuration</label>
              <p>{raw_leads_configuration || "N/A"}</p>
            </div>
            <div className="infoItem">
              <label>Buying Location</label>
              <p>{raw_leads_buying_location || "N/A"}</p>
            </div>
            <div className="infoItem fullWidth">
              <label>Budget Range</label>
              <p>
                {raw_leads_budget_min && raw_leads_budget_max
                  ? `₹${raw_leads_budget_min} - ₹${raw_leads_budget_max} Lakhs`
                  : "N/A"}
              </p>
            </div>
            <div className="infoItem fullWidth">
              <label>Customer Address</label>
              <p>{customer_address || "N/A"}</p>
            </div>
            <div className="infoItem fullWidth">
              <label>Remarks</label>
              <p>{raw_leads_remarks || "N/A"}</p>
            </div>
            {voice_note && (
              <div className="infoItem fullWidth">
                <label>Voice Note</label>
                <p>{voice_note}</p>
              </div>
            )}
          </div>
        </div>

        </div>

        {/* Property Requirements - Extract from lead type timeline item */}
        <div className="cardBox">
          <h3>Property Requirements</h3>
          {leadData.timeline.filter(item => item.type === "lead").map((leadItem, index) => (
            <div key={index} className="grid2">
              <div>
                <label>📍 Property Type</label>
                <p>{leadItem.otherDetails.property_type || "N/A"}</p>
              </div>
              <div>
                <label>💰 Budget</label>
                <p>
                  {leadItem.otherDetails.budget_min && leadItem.otherDetails.budget_max 
                    ? `₹${leadItem.otherDetails.budget_min} - ₹${leadItem.otherDetails.budget_max} ${leadItem.otherDetails.budget_type || "Lakhs"}`
                    : leadItem.otherDetails.budget || "N/A"
                  }
                </p>
              </div>
              <div>
                <label>🏠 Project</label>
                <p>{leadItem.otherDetails.project_name || "N/A"}</p>
              </div>
              <div>
                <label>⏳ Possession</label>
                <p>{leadItem.otherDetails.possession_expected || "N/A"}</p>
              </div>
              {leadItem.otherDetails.requirement && (
                <div className="fullWidth">
                  <label>📝 Requirement Details</label>
                  <p>{leadItem.otherDetails.requirement}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Timeline */}
        <div className="cardBox">
          <h3>Timeline</h3>
         
          <div className="timelineContainer">
            {leadData.timeline.map((item, index) => {
              const formattedItem = formatTimelineItem(item);
              return (
                <div key={item.id} className="timelineRow">

                  {/* vertical line */}
                  {index !== 0 && <div className="timelineLine"></div>}

                  <div className="timelineIcon">
                    {formattedItem.icon}
                  </div>

                  <div className="timelineContent">
                    <div className="timelineHeader">
                      <h4 className="timelineTitle">{formattedItem.title}</h4>
                      <span className="timelineTime">
                        {new Date(formattedItem.time).toLocaleDateString()} •{" "}
                        {new Date(formattedItem.time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>

                    <p className="timelineDescription">{formattedItem.description}</p>

                    <p className="timelineAgent">
                      👤 {formattedItem.role}
                    </p>

                    {item.otherDetails.recording_link && (
                      <a href={item.otherDetails.recording_link} target="_blank" rel="noopener noreferrer" className="recordingLink">
                        🎧 Listen to Recording
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadDetails;